// FILE: components/Header.tsx
// Enhanced header component with user avatar dropdown, notifications, search, and flexible configuration

import React, { useState } from 'react';
import { Icon } from './icons';
import { Button } from './Button';
import { Dropdown } from './Dropdown';
import { Avatar } from './Avatar';

// Define types for header props
interface HeaderLink {
  href: string;
  label: string;
}

interface UserMenuLink {
  href: string;
  label: string;
  icon: string;
}

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
}

interface LanguageOption {
  code: string;
  name: string;
  flag?: string;
}

interface HeaderProps {
  // Logo configuration
  logo?: {
    src?: string;
    text?: string;
    href?: string;
  };
  
  // Navigation links
  links?: HeaderLink[];
  
  // Search configuration
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  
  // Notifications
  showNotifications?: boolean;
  notifications?: NotificationItem[];
  onNotificationClick?: (notification: NotificationItem) => void;
  onMarkAllRead?: () => void;
  
  // User configuration
  user?: {
    name: string;
    email?: string;
    avatarUrl?: string;
    role?: string;
    isOnline?: boolean;
  };
  userMenuLinks?: UserMenuLink[];
  onLogin?: () => void;
  onSignUp?: () => void;
  onLogout?: () => void;
  
  // Language selector
  showLanguageSelector?: boolean;
  languages?: LanguageOption[];
  currentLanguage?: string;
  onLanguageChange?: (languageCode: string) => void;
  
  // Layout options
  variant?: 'website' | 'dashboard';
  sticky?: boolean;
  elevated?: boolean;
  className?: string;
}

// Notification Icon Component
const NotificationIcon: React.FC<{
  notifications?: NotificationItem[];
  onNotificationClick?: (notification: NotificationItem) => void;
  onMarkAllRead?: () => void;
}> = ({ notifications = [], onNotificationClick, onMarkAllRead }) => {
  const unreadCount = notifications.filter(n => !n.read).length;
  
  return (
    <Dropdown>
      <Dropdown.Trigger>
        <button className="relative p-2 text-text-secondary hover:text-text-primary dark:text-dark-text-secondary dark:hover:text-dark-text-primary transition-colors rounded-lg hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary">
          <Icon className="w-6 h-6">notifications</Icon>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </Dropdown.Trigger>
      <Dropdown.Content position="bottom-end" className="w-80">
        <div className="p-4 border-b border-border-color dark:border-dark-border-color">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-text-primary dark:text-dark-text-primary">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="text-sm text-brand-orange hover:text-brand-orange/80 transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-text-secondary dark:text-dark-text-secondary">
              <Icon className="w-12 h-12 mx-auto mb-2 opacity-50">notifications_off</Icon>
              <p>No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => onNotificationClick?.(notification)}
                className={`w-full p-4 text-left hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors border-b border-border-color dark:border-dark-border-color last:border-b-0 ${
                  !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notification.read ? 'bg-blue-500' : 'bg-transparent'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary dark:text-dark-text-primary truncate">
                      {notification.title}
                    </p>
                    <p className="text-sm text-text-secondary dark:text-dark-text-secondary mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-text-tertiary dark:text-dark-text-tertiary mt-2">
                      {notification.time}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        {notifications.length > 0 && (
          <div className="p-3 border-t border-border-color dark:border-dark-border-color">
            <button className="w-full text-center text-sm text-brand-orange hover:text-brand-orange/80 transition-colors py-2 rounded-md hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary">
              View all notifications
            </button>
          </div>
        )}
      </Dropdown.Content>
    </Dropdown>
  );
};

// Language Selector Component
const LanguageSelector: React.FC<{
  languages?: LanguageOption[];
  currentLanguage?: string;
  onLanguageChange?: (languageCode: string) => void;
}> = ({ languages = [], currentLanguage, onLanguageChange }) => {
  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];
  
  if (!languages.length) return null;

  return (
    <Dropdown>
      <Dropdown.Trigger>
        <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors">
          {currentLang?.flag && (
            <span className="text-lg">{currentLang.flag}</span>
          )}
          <span className="text-sm font-medium text-text-primary dark:text-dark-text-primary">
            {currentLang?.code.toUpperCase() || 'EN'}
          </span>
          <Icon className="w-4 h-4 text-text-secondary dark:text-dark-text-secondary">
            expand_more
          </Icon>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Content position="bottom-end" className="min-w-48">
        <div className="p-2">
          <div className="text-xs font-semibold text-text-secondary dark:text-dark-text-secondary uppercase tracking-wide px-3 py-2">
            Language
          </div>
          {languages.map((language) => (
            <Dropdown.Item
              key={language.code}
              onSelect={() => onLanguageChange?.(language.code)}
              className={currentLanguage === language.code ? 'bg-bg-tertiary dark:bg-dark-bg-tertiary' : ''}
            >
              <div className="flex items-center gap-3">
                {language.flag && (
                  <span className="text-lg">{language.flag}</span>
                )}
                <div className="flex flex-col">
                  <span className="font-medium">{language.name}</span>
                  <span className="text-xs text-text-secondary dark:text-dark-text-secondary">
                    {language.code.toUpperCase()}
                  </span>
                </div>
                {currentLanguage === language.code && (
                  <Icon className="w-4 h-4 text-brand-orange ml-auto">check</Icon>
                )}
              </div>
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Content>
    </Dropdown>
  );
};

// Search Bar Component
const SearchBar: React.FC<{
  placeholder?: string;
  onSearch?: (query: string) => void;
  className?: string;
}> = ({ placeholder = "Search...", onSearch, className = "" }) => {
  const [query, setQuery] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(query);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    // Optional: trigger search on every keystroke
    // onSearch?.(e.target.value);
  };
  
  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`}>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-dark-text-tertiary">
          search
        </Icon>
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-2 bg-bg-secondary dark:bg-dark-bg-secondary border border-border-color dark:border-dark-border-color rounded-lg text-text-primary dark:text-dark-text-primary placeholder-text-tertiary dark:placeholder-dark-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-colors"
        />
      </div>
    </form>
  );
};

export const Header: React.FC<HeaderProps> = ({ 
  logo,
  links = [],
  showSearch = false,
  searchPlaceholder = "Search...",
  onSearch,
  showNotifications = false,
  notifications = [],
  onNotificationClick,
  onMarkAllRead,
  user,
  userMenuLinks = [],
  onLogin = () => {},
  onSignUp = () => {},
  onLogout = () => {},
  showLanguageSelector = false,
  languages = [],
  currentLanguage,
  onLanguageChange,
  variant = 'website',
  sticky = false,
  elevated = false,
  className = ""
}) => {
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const headerClasses = `
        relative z-40 w-full transition-all duration-200
        ${sticky ? 'sticky top-0' : ''}
        ${elevated ? 'shadow-lg' : 'border-b border-border-color dark:border-dark-border-color'}
        ${variant === 'dashboard' 
            ? 'bg-white dark:bg-dark-bg-primary' 
            : 'bg-white/80 dark:bg-dark-bg-secondary/80 backdrop-blur-sm'
        }
        ${className}
    `.trim();

    const containerClasses = variant === 'dashboard' 
        ? 'w-full px-4 sm:px-6 lg:px-8' 
        : 'max-w-6xl mx-auto px-4 sm:px-6 md:px-8';

    return (
        <header className={headerClasses}>
            <div className={containerClasses}>
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <a 
                            href={logo?.href || "#"} 
                            className="flex items-center gap-2 group" 
                            aria-label={`${logo?.text || 'Acutrack'} Home`}
                        >
                            {logo?.src ? (
                                <img 
                                    src={logo.src} 
                                    alt={logo.text || 'Logo'} 
                                    className="h-8 w-auto"
                                />
                            ) : (
                                <Icon className="text-brand-orange !text-3xl group-hover:scale-105 transition-transform">
                                    track_changes
                                </Icon>
                            )}
                            <span className="text-xl lg:text-2xl font-bold">
                                <span className="text-brand-navy dark:text-white">
                                    {logo?.text || 'Acutrack'}
                                </span>
                            </span>
                        </a>
                    </div>

                    {/* Desktop Navigation & Search */}
                    <div className="hidden lg:flex items-center gap-6 flex-1 max-w-2xl mx-8">
                        {/* Navigation Links */}
                        {links.length > 0 && (
                            <nav className="flex items-center gap-1">
                                {links.map(link => (
                                    <Button 
                                        as="a" 
                                        href={link.href} 
                                        key={`${link.href}-${link.label}`} 
                                        variant="text" 
                                        size="md" 
                                        className="!font-medium text-text-primary dark:text-dark-text-primary hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary"
                                    >
                                {link.label}
                            </Button>
                        ))}
                    </nav>
                        )}

                        {/* Search Bar */}
                        {showSearch && (
                            <SearchBar
                                placeholder={searchPlaceholder}
                                onSearch={onSearch}
                                className="flex-1 max-w-md"
                            />
                        )}
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden lg:flex items-center gap-2">
                        {/* Language Selector */}
                        {showLanguageSelector && (
                            <LanguageSelector
                                languages={languages}
                                currentLanguage={currentLanguage}
                                onLanguageChange={onLanguageChange}
                            />
                        )}

                        {/* Notifications */}
                        {showNotifications && (
                            <NotificationIcon
                                notifications={notifications}
                                onNotificationClick={onNotificationClick}
                                onMarkAllRead={onMarkAllRead}
                            />
                        )}

                        {/* User Menu or Auth Buttons */}
                        {user ? (
                             <Dropdown>
                                <Dropdown.Trigger>
                                    <button 
                                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                                        aria-label="Open user menu"
                                    >
                                        <div className="relative">
                                            <Avatar name={user.name} src={user.avatarUrl} size="sm" />
                                            {user.isOnline !== undefined && (
                                                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-dark-bg-primary ${
                                                    user.isOnline ? 'bg-green-500' : 'bg-gray-400'
                                                }`} />
                                            )}
                                        </div>
                                        <div className="hidden xl:block text-left">
                                            <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">
                                                {user.name}
                                            </p>
                                            {user.role && (
                                                <p className="text-xs text-text-secondary dark:text-dark-text-secondary">
                                                    {user.role}
                                                </p>
                                            )}
                                        </div>
                                        <Icon className="w-4 h-4 text-text-secondary dark:text-dark-text-secondary">
                                            keyboard_arrow_down
                                        </Icon>
                                    </button>
                                </Dropdown.Trigger>
                                <Dropdown.Content position="bottom-end" className="w-56">
                                    {/* User Info */}
                                    <div className="px-4 py-3 border-b border-border-color dark:border-dark-border-color">
                                        <p className="font-medium text-text-primary dark:text-dark-text-primary">
                                            {user.name}
                                        </p>
                                        {user.email && (
                                            <p className="text-sm text-text-secondary dark:text-dark-text-secondary truncate">
                                                {user.email}
                                            </p>
                                        )}
                                    </div>
                                    
                                    {/* Menu Items */}
                                    {userMenuLinks.map(item => (
                                        <Dropdown.Item key={`${item.href}-${item.label}`} icon={item.icon}>
                                            <a href={item.href} className="block w-full">
                                            {item.label}
                                            </a>
                                        </Dropdown.Item>
                                    ))}
                                    
                                    {/* Logout */}
                                    <Dropdown.Separator />
                                    <Dropdown.Item icon="logout" onSelect={onLogout}>
                                        Sign out
                                    </Dropdown.Item>
                                </Dropdown.Content>
                            </Dropdown>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="text" 
                                    size="md" 
                                    className="!font-medium" 
                                    onClick={onLogin}
                                >
                                    Log In
                                </Button>
                                <Button 
                                    variant="primary" 
                                    size="md" 
                                    onClick={onSignUp}
                                >
                                    Get Started
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="lg:hidden flex items-center gap-2">
                        {/* Mobile Language Selector & Notifications */}
                        {showLanguageSelector && (
                            <LanguageSelector
                                languages={languages}
                                currentLanguage={currentLanguage}
                                onLanguageChange={onLanguageChange}
                            />
                        )}
                        
                        {showNotifications && (
                            <NotificationIcon
                                notifications={notifications}
                                onNotificationClick={onNotificationClick}
                                onMarkAllRead={onMarkAllRead}
                            />
                        )}
                        
                        <Button 
                            variant="text" 
                            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)} 
                            className="text-text-primary dark:text-dark-text-primary hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary"
                        >
                            <Icon>{isMobileMenuOpen ? 'close' : 'menu'}</Icon>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="lg:hidden border-t border-border-color dark:border-dark-border-color absolute top-full left-0 w-full bg-white dark:bg-dark-bg-primary shadow-lg">
                    <div className="p-4 space-y-4">
                        {/* Mobile Search */}
                        {showSearch && (
                            <SearchBar
                                placeholder={searchPlaceholder}
                                onSearch={onSearch}
                            />
                        )}

                        {/* Mobile Navigation */}
                        {links.length > 0 && (
                            <nav className="space-y-1">
                                {links.map(link => (
                                    <a 
                                        href={link.href} 
                                        key={`${link.href}-${link.label}`} 
                                        className="block px-4 py-3 rounded-lg font-medium text-text-primary dark:text-dark-text-primary hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                {link.label}
                            </a>
                        ))}
                            </nav>
                        )}

                        {/* Mobile User Section */}
                        <div className="pt-4 border-t border-border-color dark:border-dark-border-color">
                             {user ? (
                                <div className="space-y-3">
                                <div className="flex items-center gap-3 px-4 py-2">
                                    <div className="relative">
                                        <Avatar name={user.name} src={user.avatarUrl} size="sm" />
                                        {user.isOnline !== undefined && (
                                            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-dark-bg-primary ${
                                                user.isOnline ? 'bg-green-500' : 'bg-gray-400'
                                            }`} />
                                        )}
                                    </div>
                                        <div>
                                            <p className="font-medium text-text-primary dark:text-dark-text-primary">
                                                {user.name}
                                            </p>
                                            {user.email && (
                                                <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
                                                    {user.email}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {userMenuLinks.map(item => (
                                        <a
                                            key={`${item.href}-${item.label}`}
                                            href={item.href}
                                            className="flex items-center gap-3 px-4 py-2 text-text-primary dark:text-dark-text-primary hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary rounded-lg transition-colors"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            <Icon className="w-5 h-5">{item.icon}</Icon>
                                            {item.label}
                                        </a>
                                    ))}
                                    
                                    <button
                                        onClick={() => {
                                            onLogout();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="flex items-center gap-3 px-4 py-2 w-full text-left text-text-primary dark:text-dark-text-primary hover:bg-bg-secondary dark:hover:bg-dark-bg-secondary rounded-lg transition-colors"
                                    >
                                        <Icon className="w-5 h-5">logout</Icon>
                                        Sign out
                                    </button>
                                </div>
                             ) : (
                                <div className="space-y-2">
                                    <Button 
                                        variant="secondary" 
                                        onClick={() => {
                                            onLogin();
                                            setMobileMenuOpen(false);
                                        }} 
                                        className="w-full"
                                    >
                                        Log In
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        onClick={() => {
                                            onSignUp();
                                            setMobileMenuOpen(false);
                                        }} 
                                        className="w-full"
                                    >
                                        Get Started
                                    </Button>
                                </div>
                             )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};